import { calculateFamilyPrice, colors } from '@lmc/common/constants'
import { lowerCase } from 'lodash'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { mixpanel } from '../../../common/services/analytics'
import { trackEvent } from '../../../common/services/intercom'
import { hexToRgbA } from '../../../common/utils'
import SingleCheckboxField from '../../../components/fields/types/SingleCheckboxField'
import { OldButton } from '../../../components/generic'
import { FamilyPaymentOption } from './components'

const renderPaywallSectionHeader = (title) => (
  <PaywallSectionHeader>{title}</PaywallSectionHeader>
)

const renderInviteBox = (openModal) => (
  <InviteBox>
    <FamilyLogo
      alt=""
      src="https://lmc-data-production.s3.eu-west-2.amazonaws.com/icons/family/Family%26Friends+Icon+1.png"
    />
    <InviteHeader>Family App</InviteHeader>
    <InviteBoxMainText $marginBottom={8}>
      The Family App is designed to help relatives keep in touch with their
      loved ones. This gives families <br />
      their own app, which provides them with access to their relatives' care
      notes and records.
    </InviteBoxMainText>
    <InviteBoxMainText>
      To start inviting families to the Family App, you'll need to set up the
      Family App add-on.
    </InviteBoxMainText>
    <ButtonContainer $marginTop={21}>
      <OldButton
        onClick={openModal}
        type="add"
        text="Set up Family App"
        style={familySetupBtnStyle}
      />
    </ButtonContainer>
    <MainTextBulletPoints $margin="21px 0 31px 0">
      Make sure you know: <br />• How you would like to personalise the apps{' '}
      <br />• Who the Family App will be billed to
    </MainTextBulletPoints>
  </InviteBox>
)

// Benefits Section

const renderBenefitsItem = (iconUrl, description, key) => (
  <BenefitsItem key={key}>
    <BenefitsItemIcon alt="" src={iconUrl} />
    <BenefitsItemDescription>{description}</BenefitsItemDescription>
  </BenefitsItem>
)

const renderBenefitsSection = () => {
  const benefitsFirstRowData = [
    {
      iconUrl:
        'https://lmc-data-production.s3.eu-west-2.amazonaws.com/icons/best+1.png',
      description:
        'Demonstrate outstanding care, innovation and family involvement'
    },
    {
      iconUrl:
        'https://lmc-data-production.s3.eu-west-2.amazonaws.com/icons/survey.png',
      description: 'Choose what data is shown to meet your own data compliance'
    },
    {
      iconUrl:
        'https://lmc-data-production.s3.eu-west-2.amazonaws.com/icons/phone.png',
      description: 'Lower customer service requirements on key staff'
    }
  ]

  const benefitsSecondRowData = [
    {
      iconUrl:
        'https://lmc-data-production.s3.eu-west-2.amazonaws.com/icons/meeting.png',
      description: 'Reduce need for face to face meetings and phone calls'
    },
    {
      iconUrl:
        'https://lmc-data-production.s3.eu-west-2.amazonaws.com/icons/camera.png',
      description: 'Share happy moments with attached photos'
    },
    {
      iconUrl:
        'https://lmc-data-production.s3.eu-west-2.amazonaws.com/icons/contact+1.png',
      description:
        'Help keep loved ones in touch with clients and give them peace of mind'
    }
  ]

  const sectionTitle = 'What are the benefits for care services?'

  return (
    <SectionContainer>
      {renderPaywallSectionHeader(sectionTitle)}
      <BenefitsRow>
        {benefitsFirstRowData.map((item, index) =>
          renderBenefitsItem(item.iconUrl, item.description, index)
        )}
      </BenefitsRow>
      <BenefitsRow>
        {benefitsSecondRowData.map((item, index) =>
          renderBenefitsItem(item.iconUrl, item.description, index)
        )}
      </BenefitsRow>
    </SectionContainer>
  )
}

// Features Section

const TryItOutDiagram = () => {
  const logCategories = [
    {
      label: 'Activities & Social',
      values: { isChecked: true }
    },
    {
      label: 'Clinical Care',
      values: { isChecked: false }
    },
    {
      label: 'Handovers',
      values: { isChecked: false }
    },
    {
      label: 'Health Visit',
      values: { isChecked: true }
    },
    {
      label: 'Personal Care',
      values: { isChecked: false }
    }
  ]

  const [isChecked, setIsChecked] = useState(true)
  const imgUrl = isChecked
    ? 'https://lmc-data-production.s3.eu-west-2.amazonaws.com/icons/family/foodon.png'
    : 'https://lmc-data-production.s3.eu-west-2.amazonaws.com/icons/family/foodoff.png'

  return (
    <FeaturesTryItOutDiagram>
      <TryItOutContainer>
        <TryItOutImage
          alt=""
          src="https://lmc-data-production.s3.eu-west-2.amazonaws.com/icons/family/tryitout.png"
        />
      </TryItOutContainer>
      <LogCategoryBtnsContainer>
        <div>
          <SingleCheckboxField
            compact
            label="Food & Drink"
            formKey="foodAndDrink"
            values={{ foodAndDrink: isChecked }}
            onChange={(output) => {
              setIsChecked(output.value)
            }}
          />
        </div>
        {logCategories.map((category) => (
          <LogCategoryBtn key={category.label}>
            <SingleCheckboxField
              compact
              label={category.label}
              values={category.values}
              formKey="isChecked"
            />
          </LogCategoryBtn>
        ))}
        <LogCategoryBtnsMask />
      </LogCategoryBtnsContainer>
      <PhoneImgContainer>
        <PhoneImg alt="" src={imgUrl} />
      </PhoneImgContainer>
      <WhatFamiliesSeeContainer>
        <WhatFamiliesSeeImage
          alt=""
          src="https://lmc-data-production.s3.eu-west-2.amazonaws.com/icons/family/whatfamiliessee.png"
        />
      </WhatFamiliesSeeContainer>
    </FeaturesTryItOutDiagram>
  )
}

const renderFeaturesSection = (openModal, preferredPeopleNominative) => {
  const sectionTitle = `What features does it provide to the families of your ${lowerCase(
    preferredPeopleNominative
  )}?`
  return (
    <WhiteSectionContainer>
      {renderPaywallSectionHeader(sectionTitle)}
      <MainText>
        Families and friends can log in via their own app and see a snapshot of
        important care related tasks, food, activities and health visits
        recorded as a timeline. They'll also be able to see what medications
        their loved one is currently taking and additional information that your
        front-line care team records using their "Family Update" log.
      </MainText>
      <PersonalisationText>
        You can choose which information should be shared with the family.{' '}
        <br />
        You can best personalise the App to fit your care service.
      </PersonalisationText>
      <TryItOutDiagram />
      <AppPersonalisationBtnContainer>
        <a href={APP_PERSONALISATION_LINK} target="_blank" rel="noreferrer">
          <OldButton
            text="Learn more about App personalisation"
            type="cancel"
            style={pinkButtonStyle}
          />
        </a>
      </AppPersonalisationBtnContainer>
      <FeaturesSetupBtn>
        <OldButton
          onClick={openModal}
          type="add"
          text="Set up Family App"
          style={familySetupBtnStyle}
        />
      </FeaturesSetupBtn>
    </WhiteSectionContainer>
  )
}

// Cost Section

const renderCostSection = (
  openModal,
  activeResidents,
  preferredPeopleNominative
) => {
  const sectionTitle = 'How much does the Family App cost?'

  // if (!activeResidents) { return null }

  return (
    <SectionContainer>
      {renderPaywallSectionHeader(sectionTitle)}
      <PaymentOptionsContainer>
        <FamilyPaymentOption
          payee="Care Service billed monthly"
          costPerMonth={calculateFamilyPrice(activeResidents)}
          activeResidents={activeResidents}
          imgUrl="https://lmc-data-production.s3.eu-west-2.amazonaws.com/icons/family/nursing-home-free-trial.png"
          mainText={`The price is based on the number of active ${lowerCase(
            preferredPeopleNominative
          )} in your service.`}
          bulletPoint1="Offer as a benefit to as many families as you like"
          bulletPoint2="Demonstrate outstanding innovation and family involvement"
          bulletPoint3="Families don't need to pay"
          preferredPeopleNominative={lowerCase(preferredPeopleNominative)}
        />
      </PaymentOptionsContainer>
      <CostLearnMoreBtn>
        <a href={PRICING_HELP_LINK} target="_blank" rel="noreferrer">
          <OldButton
            text="Learn more about pricing"
            type="cancel"
            style={pinkButtonStyle}
          />
        </a>
      </CostLearnMoreBtn>
      <CostSetupBtn>
        <OldButton
          onClick={openModal}
          type="add"
          text="Set up Family App"
          style={familySetupBtnStyle}
        />
      </CostSetupBtn>
    </SectionContainer>
  )
}

// Security section

const renderSecuritySection = (openModal) => {
  const sectionTitle = 'Secure & compliant from the outset'
  return (
    <WhiteSectionContainer>
      {renderPaywallSectionHeader(sectionTitle)}
      <MainText>
        We take data security very seriously as with the rest of our care
        system, the Family App is secured to NHS standards and GDPR compliant.
        However, we know this isn't always enough, that's why your Managers have
        full access over which Family App users can see information through the
        app, switching it on or off as required.
      </MainText>
      <SecuritySetupBtn>
        <OldButton
          onClick={openModal}
          type="add"
          text="Set up Family App"
          style={familySetupBtnStyle}
        />
      </SecuritySetupBtn>
      <MainTextBulletPoints>
        Make sure you know <br />• How you would like to personalise the apps{' '}
        <br />• Who the Family App will be billed to
      </MainTextBulletPoints>
    </WhiteSectionContainer>
  )
}

export default function FamilyPortalPaywall(props) {
  const { activeResidents, openModal, preferredPeopleNominative } = props

  useEffect(() => {
    trackEvent('CO_family_view_paywall')
    mixpanel.track('CO_family_view_paywall')
  }, [])

  const openFamilyModal = () => {
    trackEvent('CO_family_open_modal')
    mixpanel.track('CO_family_open_modal')
    openModal()
  }

  return (
    <Wrapper>
      <MaskLayer />
      <MainContainer>
        {renderInviteBox(openFamilyModal)}
        {renderBenefitsSection()}
        {renderFeaturesSection(openFamilyModal, preferredPeopleNominative)}
        {renderCostSection(
          openFamilyModal,
          activeResidents,
          preferredPeopleNominative
        )}
        {renderSecuritySection(openFamilyModal)}
      </MainContainer>
    </Wrapper>
  )
}

const APP_PERSONALISATION_LINK =
  'https://support.logmycare.co.uk/en/articles/4248185-how-can-i-customise-the-family-app'
const PRICING_HELP_LINK =
  'https://support.logmycare.co.uk/en/articles/4448809-how-much-does-the-family-app-cost'

// Style objects for components that accept style props
const pinkButtonStyle = {
  color: colors.pink,
  background: colors.white,
  border: `1px solid ${colors.pink}`,
  boxShadow: `0 0 8px ${hexToRgbA(colors.black, 0.2)}`
}

const familySetupBtnStyle = {
  height: 40,
  boxShadow: `0 0 8px ${hexToRgbA(colors.black, 0.2)}`
}

const Wrapper = styled.div`
  position: relative;
  top: -140px;
  width: 100%;
  height: 100%;
  max-width: 1080px;
  margin-bottom: -370px;
`

const MaskLayer = styled.div`
  height: 185px;
  background: ${colors.background};
  opacity: 0.8;
`

const MainContainer = styled.div`
  background: ${colors.background};
  height: 100%;
`

const PaywallSectionHeader = styled.h2`
  line-height: 36px;
  text-align: center;
  margin: 0 auto;
`

const InviteBox = styled.div`
  align-items: center;
  background: ${colors.white};
  border-radius: 4px;
  border: 1px solid ${colors.grey};
  display: flex;
  flex-direction: column;
`

const FamilyLogo = styled.img`
  height: 63px;
  margin-top: 22px;
  margin-bottom: 17px;
`

const InviteHeader = styled.h2`
  line-height: 36px;
  margin-bottom: 15px;
`

const InviteBoxMainText = styled.div`
  line-height: 22px;
  text-align: center;
  ${(props) =>
    props.$marginBottom && `margin-bottom: ${props.$marginBottom}px;`}
`

const ButtonContainer = styled.div`
  ${(props) => props.$marginTop && `margin-top: ${props.$marginTop}px;`}
`

const MainTextBulletPoints = styled.div`
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  ${(props) => props.$margin && `margin: ${props.$margin};`}
`

const BenefitsItem = styled.div`
  width: 235px;
  display: flex;
  align-items: center;
  flex-direction: column;
  margin: 0 40px 0 40px;
`

const BenefitsItemIcon = styled.img`
  height: 84px;
`

const BenefitsItemDescription = styled.div`
  line-height: 22px;
  margin-top: 16px;
  text-align: center;
`

const BenefitsRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 40px;
`

const SectionContainer = styled.div`
  padding-top: 64px;
  padding-bottom: 64px;
`

const WhiteSectionContainer = styled(SectionContainer)`
  margin-left: min(-50vw + 540px - 24px, -24px);
  padding-left: max(50vw - 540px + 24px, 24px);
  margin-right: min(-50vw + 540px - 24px, -24px);
  padding-right: max(50vw - 540px + 24px, 24px);
  background: ${colors.white};
`

const MainText = styled.div`
  line-height: 22px;
  margin-top: 16px;
  text-align: center;
`

const PersonalisationText = styled.div`
  margin-top: 32px;
  font-size: 18px;
  line-height: 32px;
  text-align: center;
`

const FeaturesTryItOutDiagram = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
`

const TryItOutContainer = styled.div`
  position: relative;
  left: -18.5px;
  top: 33px;
`

const TryItOutImage = styled.img`
  width: 89px;
`

const LogCategoryBtnsContainer = styled.div`
  margin-top: 20px;
  margin-right: 17.5px;
  max-height: 400px;
`

const LogCategoryBtn = styled.div`
  margin-top: 10px;
`

const LogCategoryBtnsMask = styled.div`
  background: linear-gradient(${hexToRgbA(colors.white, 0.7)}, ${colors.white});
  height: 280px;
  position: relative;
  top: -235px;
`

const PhoneImgContainer = styled.div`
  margin-left: 17.5px;
`

const PhoneImg = styled.img`
  width: 224px;
`

const WhatFamiliesSeeContainer = styled.div`
  position: relative;
  left: 20px;
  top: 147px;
`

const WhatFamiliesSeeImage = styled.img`
  width: 106px;
`

const AppPersonalisationBtnContainer = styled.div`
  margin-top: 28px;
  display: flex;
  justify-content: center;
`

const FeaturesSetupBtn = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`

const PaymentOptionsContainer = styled.div`
  margin-top: 32px;
  display: flex;
  justify-content: center;
`

const CostLearnMoreBtn = styled.div`
  padding: 50px 0 16px 0;
  display: flex;
  justify-content: center;
`

const CostSetupBtn = styled.div`
  display: flex;
  justify-content: center;
`

const SecuritySetupBtn = styled.div`
  padding: 40px 0 8px 0;
  display: flex;
  justify-content: center;
`
